/** 断点记录库名。只存在当前浏览器，换浏览器后需要重新选择文件。 */
const UPLOAD_RESUME_DB = 'cinematicgaming-upload';
/** 断点库版本。2 起增加文件句柄表，不改已有断点表。 */
const UPLOAD_RESUME_DB_VERSION = 2;
/** 断点记录表名，主键是素材 ID。 */
const UPLOAD_RESUME_STORE = 'multipart-uploads';
/** 文件句柄表名，主键是素材 ID。只存句柄，不存文件字节。 */
const UPLOAD_FILE_HANDLE_STORE = 'upload-file-handles';
/**
 * 打开断点库。浏览器没有 IndexedDB 时返回空，上传仍可在本次页面内进行。
 * @returns 数据库；不可用时为空
 */
function openUploadResumeDb() {
    if (typeof indexedDB === 'undefined')
        return Promise.resolve(null);
    return new Promise((resolve, reject) => {
        /** 打开数据库的请求。 */
        const request = indexedDB.open(UPLOAD_RESUME_DB, UPLOAD_RESUME_DB_VERSION);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(UPLOAD_RESUME_STORE)) {
                db.createObjectStore(UPLOAD_RESUME_STORE, { keyPath: 'assetId' });
            }
            if (!db.objectStoreNames.contains(UPLOAD_FILE_HANDLE_STORE)) {
                db.createObjectStore(UPLOAD_FILE_HANDLE_STORE, { keyPath: 'assetId' });
            }
        };
        request.onsuccess = () => {
            resolve(request.result);
        };
        request.onerror = () => {
            reject(request.error ?? new Error('打开上传断点库失败'));
        };
    });
}
/**
 * 读取全部未完成的分片断点。
 * @returns 断点记录；读取失败时为空数组，不阻断页面
 */
export async function loadUploadResumeRecords() {
    try {
        const db = await openUploadResumeDb();
        if (!db)
            return [];
        return await new Promise((resolve, reject) => {
            const transaction = db.transaction(UPLOAD_RESUME_STORE, 'readonly');
            const request = transaction.objectStore(UPLOAD_RESUME_STORE).getAll();
            request.onsuccess = () => {
                const stored = request.result;
                resolve(stored ?? []);
            };
            request.onerror = () => {
                reject(request.error ?? new Error('读取上传断点失败'));
            };
        });
    }
    catch {
        return [];
    }
}
/**
 * 保存或覆盖一条断点。
 * @param record 当前分片进度
 */
export async function saveUploadResumeRecord(record) {
    const db = await openUploadResumeDb();
    if (!db)
        return;
    await new Promise((resolve, reject) => {
        const transaction = db.transaction(UPLOAD_RESUME_STORE, 'readwrite');
        const request = transaction.objectStore(UPLOAD_RESUME_STORE).put(record);
        request.onsuccess = () => {
            resolve();
        };
        request.onerror = () => {
            reject(request.error ?? new Error('保存上传断点失败'));
        };
    });
}
/**
 * 读取已保存的文件句柄。页面加载时只取出句柄，不读取文件内容。
 * @returns 素材 ID 到句柄的映射；读取失败时为空
 */
export async function loadUploadFileHandles() {
    /** 还能用来续传的句柄。 */
    const handles = new Map();
    try {
        const db = await openUploadResumeDb();
        if (!db?.objectStoreNames.contains(UPLOAD_FILE_HANDLE_STORE))
            return handles;
        const stored = await new Promise((resolve, reject) => {
            const transaction = db.transaction(UPLOAD_FILE_HANDLE_STORE, 'readonly');
            const request = transaction.objectStore(UPLOAD_FILE_HANDLE_STORE).getAll();
            request.onsuccess = () => {
                resolve(Array.isArray(request.result) ? request.result : []);
            };
            request.onerror = () => {
                reject(request.error ?? new Error('读取上传文件句柄失败'));
            };
        });
        stored.forEach((item) => {
            if (!isStoredUploadFileHandle(item))
                return;
            handles.set(item.assetId, item.handle);
        });
    }
    catch {
        return handles;
    }
    return handles;
}
/**
 * 入队时保存文件句柄，供刷新后点击重试读回原文件。
 * @param assetId 占位素材 ID
 * @param handle 本次选择拿到的句柄
 */
export async function saveUploadFileHandle(assetId, handle) {
    const db = await openUploadResumeDb();
    if (!db?.objectStoreNames.contains(UPLOAD_FILE_HANDLE_STORE))
        return;
    const record = { assetId, handle };
    await new Promise((resolve, reject) => {
        const transaction = db.transaction(UPLOAD_FILE_HANDLE_STORE, 'readwrite');
        const request = transaction.objectStore(UPLOAD_FILE_HANDLE_STORE).put(record);
        request.onsuccess = () => {
            resolve();
        };
        request.onerror = () => {
            reject(request.error ?? new Error('保存上传文件句柄失败'));
        };
    });
}
/**
 * 句柄失效或用户改走重新选择时，只删除句柄，保留分片断点。
 * @param assetId 占位素材 ID
 */
export async function deleteUploadFileHandle(assetId) {
    const db = await openUploadResumeDb();
    if (!db?.objectStoreNames.contains(UPLOAD_FILE_HANDLE_STORE))
        return;
    await new Promise((resolve, reject) => {
        const transaction = db.transaction(UPLOAD_FILE_HANDLE_STORE, 'readwrite');
        const request = transaction.objectStore(UPLOAD_FILE_HANDLE_STORE).delete(assetId);
        request.onsuccess = () => {
            resolve();
        };
        request.onerror = () => {
            reject(request.error ?? new Error('删除上传文件句柄失败'));
        };
    });
}
/**
 * 上传完成、取消或删除素材后删除断点和文件句柄。
 * @param assetId 占位素材 ID
 */
export async function deleteUploadResumeRecord(assetId) {
    const db = await openUploadResumeDb();
    if (!db)
        return;
    /** 同一次事务里删掉断点和句柄，避免任务结束后还留着句柄。 */
    const storeNames = [UPLOAD_RESUME_STORE];
    if (db.objectStoreNames.contains(UPLOAD_FILE_HANDLE_STORE)) {
        storeNames.push(UPLOAD_FILE_HANDLE_STORE);
    }
    await new Promise((resolve, reject) => {
        const transaction = db.transaction(storeNames, 'readwrite');
        transaction.objectStore(UPLOAD_RESUME_STORE).delete(assetId);
        if (storeNames.includes(UPLOAD_FILE_HANDLE_STORE)) {
            transaction.objectStore(UPLOAD_FILE_HANDLE_STORE).delete(assetId);
        }
        transaction.oncomplete = () => {
            resolve();
        };
        transaction.onerror = () => {
            reject(transaction.error ?? new Error('删除上传断点失败'));
        };
    });
}
/** 判断 IndexedDB 读出的值是不是文件句柄记录。 */
function isStoredUploadFileHandle(value) {
    if (typeof value !== 'object' || value == null)
        return false;
    const record = value;
    return typeof record.assetId === 'number' && record.handle != null && typeof record.handle.getFile === 'function';
}
/**
 * 把分片区间转成断点里保存的分片号。仅类型复用，避免队列和存储各写一套分片结构。
 * @param range 分片区间
 * @param etag 该分片的 ETag
 * @returns 断点分片
 */
export function toResumePart(range, etag) {
    return { partNumber: range.partNumber, etag };
}
